class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
    this.autoScrollMain = false;
    this.suppressMainScrollCancel = false;
    this.autoScrollDisableTimer = null;
    this.onDrawerToggle = this.onDrawerToggle.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onMainScroll = this.onMainScroll.bind(this);
    this.onMainScrollSync = this.onMainScrollSync.bind(this);
    this.onDrawerOpened = this.onDrawerOpened.bind(this);
    this.onCheckoutClick = this.onCheckoutClick.bind(this);
    this.viewCartPending = false;
  }

  connectedCallback() {
    this.drawer = document.querySelector('cart-drawer');
    this.drawerDetails = this.drawer?.querySelector('details');

    this.ensureDrawerPortal();

    if (this.drawerDetails) {
      this.drawerDetails.addEventListener('toggle', this.onDrawerToggle);
    }

    window.visualViewport?.addEventListener('resize', this.onViewportChange);
    window.addEventListener('resize', this.onViewportChange);
    document.addEventListener('cartdrawer:main-scroll-sync', this.onMainScrollSync);
    document.addEventListener('cartdrawer:opened', this.onDrawerOpened);
    document.addEventListener('click', this.onCheckoutClick, true);
  }

  disconnectedCallback() {
    this.drawerDetails?.removeEventListener('toggle', this.onDrawerToggle);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    document.removeEventListener('cartdrawer:main-scroll-sync', this.onMainScrollSync);
    document.removeEventListener('cartdrawer:opened', this.onDrawerOpened);
    document.removeEventListener('click', this.onCheckoutClick, true);
  }

  // Measurement: GA4 through the page's own gtag (consent mode decides what is sent), Clarity only once
  // it has loaded (after consent), and a dataLayer event for GTM. No new tags, no consent calls.
  track(name, params = {}, { ga4 = true } = {}) {
    try {
      if (ga4 && typeof window.gtag === 'function') window.gtag('event', name, { send_to: 'G-GMPTV2T4PW', ...params });
      if (typeof window.clarity === 'function') window.clarity('event', name);
      (window.dataLayer = window.dataLayer || []).push({ event: `yanaa_${name}`, yanaa: params });
    } catch (_) {}
  }

  cartSnapshot() {
    const items = [...this.querySelectorAll('li[data-variant-id]')].map((line) => ({
      item_id: line.dataset.variantId,
      item_name: line.dataset.handle,
      quantity: Number(line.dataset.quantity) || 1,
      price: (Number(line.dataset.price) || 0) / 100,
    }));
    const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;
    return { currency: window.Shopify?.currency?.active, value, items };
  }

  // One view_cart per closed -> open transition, sent once the drawer content is in place;
  // cartdrawer:opened fires 2-3 times per open and on content swaps, so it is gated by the flag.
  onDrawerOpened() {
    if (!this.viewCartPending || !this.drawerDetails?.hasAttribute('open')) return;
    this.viewCartPending = false;
    this.track('view_cart', this.cartSnapshot());
  }

  onCheckoutClick(event) {
    if (!(event.target instanceof Element) || !event.target.closest('#mini-cart button[name="checkout"]')) return;
    this.track('drawer_checkout_click', {});
  }

  onViewportChange() {
    if (!document.body.classList.contains('mini-cart--open')) return;
    this.syncDrawerLayout();
  }

  armAutoScrollMain() {
    this.autoScrollMain = true;
    clearTimeout(this.autoScrollDisableTimer);
    // Keep auto-scroll active through the whole open sequence. Recommendations
    // load lazily (~0.5-1.2s after open) and expand the scroll area, so the
    // earlier "reached bottom" state is only temporary — we must keep scrolling
    // to the new bottom until things settle. After that, stop so later content
    // updates (e.g. quantity changes) don't yank the scroll position.
    this.autoScrollDisableTimer = setTimeout(() => {
      this.autoScrollMain = false;
    }, 1800);
  }

  onDrawerToggle() {
    if (!this.drawerDetails?.hasAttribute('open')) {
      clearTimeout(this.autoScrollDisableTimer);
      this.autoScrollMain = false;
      theme.clearPageScrollLock?.();
      return;
    }

    this.armAutoScrollMain();
    this.viewCartPending = true;

    if (!this.loaded) {
      this.loadMiniCart();
      return;
    }

    this.syncDrawerLayout();
    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
  }

  loadMiniCart(attempt = 1) {
    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error(`mini-cart section HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const miniCart = document.getElementById('mini-cart');
        if (!miniCart) return;

        miniCart.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
        this.loaded = true;
        this.afterContentSwap();
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch((e) => {
        console.error(e);
        // Retry once, then give the shopper a way to the cart page instead of an endless spinner.
        if (attempt < 2) {
          setTimeout(() => this.loadMiniCart(attempt + 1), 1000);
          return;
        }
        this.showLoadError();
      });
  }

  showLoadError() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart || miniCart.querySelector('.mini-cart__load-error')) return;

    const fr = (document.documentElement.lang || '').toLowerCase().startsWith('fr');
    const root = window.Shopify?.routes?.root || '/';
    const box = document.createElement('div');
    box.className = 'mini-cart__load-error center';
    box.setAttribute('role', 'alert');
    box.innerHTML = `<p>${fr ? 'Le panier ne s’est pas chargé.' : 'Your cart didn’t load.'}</p>`
      + `<a class="button" href="${root}cart">${fr ? 'Voir le panier' : 'View cart'}</a>`;
    // The first-open spinner would sit on top of the message (and keep animating): hide it.
    miniCart.querySelectorAll('.loading-overlay').forEach((node) => node.classList.add('hidden'));
    miniCart.prepend(box);
  }

  open() {
    const detailsElement = this.drawer?.querySelector('details');
    if (!detailsElement) return;

    this.armAutoScrollMain();

    if (detailsElement.hasAttribute('open')) {
      this.afterContentSwap();
      return;
    }

    this.drawer.openMenuDrawer();
    setTimeout(() => this.afterContentSwap(), 50);
  }

  renderContents(parsedState) {
    document.querySelector('cart-drawer')?.suppressFocusOutClose?.(400);

    this.productId = parsedState.id;
    // renderContents only runs after a successful /cart/add (GA4 add_to_cart already comes from Shopify).
    if (parsedState && !parsedState.status) {
      this.track('atc_success', { handle: parsedState.handle, variant_id: parsedState.variant_id }, { ga4: false });
      try { window.clarity?.('set', 'atc_product', String(parsedState.handle || '')); } catch (_) {}
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml || !document.getElementById(section.id)) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    this.loaded = true;
    document.dispatchEvent(new CustomEvent('cartdrawer:content-updated'));

    if (typeof setHeaderBottomPosition === 'function') {
      setHeaderBottomPosition();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.open();
      });
    });
  }

  afterContentSwap() {
    if (typeof setHeaderBottomPosition === 'function') {
      setHeaderBottomPosition();
    }

    this.syncDrawerLayout();
    this.bindMainScrollBehavior();

    requestAnimationFrame(() => {
      this.syncDrawerLayout();
      this.syncMainScroll({ scrollToBottom: this.autoScrollMain });
      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    });
  }

  onMainScrollSync() {
    this.syncMainScroll({ scrollToBottom: this.autoScrollMain });
  }

  onMainScroll(event) {
    const main = event.currentTarget;
    this.updateMainScrollClasses(main);

    if (!this.autoScrollMain || this.suppressMainScrollCancel) return;

    const maxScroll = main.scrollHeight - main.clientHeight;
    if (main.scrollTop < maxScroll - 12) {
      this.autoScrollMain = false;
    }
  }

  bindMainScrollBehavior() {
    const main = document.querySelector('#mini-cart .mini-cart__main');
    if (!main || main.dataset.mainScrollBound === 'true') return;

    main.dataset.mainScrollBound = 'true';

    main.addEventListener('scroll', this.onMainScroll, { passive: true });
    main.addEventListener('wheel', () => { this.autoScrollMain = false; }, { passive: true });
    main.addEventListener('touchstart', () => { this.autoScrollMain = false; }, { passive: true });

    if (!main._mainScrollResizeObserver) {
      main._mainScrollResizeObserver = new ResizeObserver(() => {
        this.updateMainScrollClasses(main);
      });
      main._mainScrollResizeObserver.observe(main);
    }
  }

  syncMainScroll({ scrollToBottom = false } = {}) {
    const main = document.querySelector('#mini-cart .mini-cart__main');
    if (!main) return;

    this.bindMainScrollBehavior();

    if (scrollToBottom && this.shouldAutoScrollMainToBottom()) {
      this.scrollMainToBottom(main);
    }

    this.updateMainScrollClasses(main);
  }

  scrollMainToBottom(main) {
    const previousSnap = main.style.scrollSnapType;
    const previousBehavior = main.style.scrollBehavior;

    this.suppressMainScrollCancel = true;
    main.style.scrollSnapType = 'none';
    main.style.scrollBehavior = 'auto';
    main.scrollTop = Math.max(0, main.scrollHeight - main.clientHeight);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        main.scrollTop = Math.max(0, main.scrollHeight - main.clientHeight);
        main.style.scrollSnapType = previousSnap;
        main.style.scrollBehavior = previousBehavior;
        this.updateMainScrollClasses(main);

        // Note: autoScrollMain is NOT cleared here. The pre-recommendations content
        // can briefly look "at the bottom" before the lazy recommendations expand the
        // scroll area, so clearing here would freeze the scroll near the top. Instead
        // the open-sequence timer (armAutoScrollMain) and genuine user interaction
        // (wheel / touch / scroll-up) are what stop auto-scrolling.
        this.suppressMainScrollCancel = false;
      });
    });
  }

  shouldAutoScrollMainToBottom() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  updateMainScrollClasses(main) {
    const scrollable = main.scrollHeight - main.clientHeight > 1;
    const scrolledFromTop = main.scrollTop > 1;

    main.classList.toggle('is-scrollable', scrollable);
    main.classList.toggle('is-scrolled-from-top', scrollable && scrolledFromTop);
  }

  syncDrawerLayout() {
    const drawer = document.getElementById('mini-cart');
    if (!drawer) return;

    const viewportHeight = Math.round(window.visualViewport?.height ?? window.innerHeight);
    drawer.style.setProperty('--max-height', `${viewportHeight}px`);
    drawer.style.removeProperty('height');
    drawer.style.removeProperty('min-height');

    this.syncMainScroll({ scrollToBottom: this.autoScrollMain });
  }

  ensureDrawerPortal() {
    if (this.id !== 'mini-cart' || this.dataset.drawerPortaled === 'true') return;

    // Set the flag BEFORE moving. Appending re-runs connectedCallback
    // synchronously in some engines; without the flag set first this recurses
    // until the stack overflows and the cart script dies.
    this.dataset.drawerPortaled = 'true';

    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return section ? section.innerHTML : '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);

const CART_WIDGET_HIDDEN = 'data-cart-drawer-widget-hidden';
const DRAWER_BODY_CLASSES = [
  'mini-cart--open',
  'mini-cart--opening',
  'mini-cart--closing',
  'menu-mobile--open',
  'menu-mobile--opening',
  'menu-mobile--closing',
];

function isDrawerOverlayActive() {
  return DRAWER_BODY_CLASSES.some((className) => document.body.classList.contains(className));
}

function drawerBodyClassChanged(oldValue = '', newValue = document.body.className) {
  return DRAWER_BODY_CLASSES.some(
    (className) => oldValue.includes(className) !== newValue.includes(className)
  );
}

// Klaviyo pop-ups are found by stable hooks (a role="dialog" that holds form.klaviyo-form, or a
// Klaviyo .needsclick dialog), never by Klaviyo's hashed reset class, which changes without notice.
const KL_STALE = 'data-yanaa-kl-stale';
const KL_CLOSING = 'data-yanaa-kl-closing';
let klaviyoLastOpen = 0;
let klaviyoLastClose = 0;

function getKlaviyoPopups() {
  return [...document.querySelectorAll('[role="dialog"]')].filter((node) => (
    (node.querySelector('form.klaviyo-form') || node.classList.contains('needsclick'))
    && !node.closest('[class*="kl-teaser"]')
    && !node.querySelector('[class*="kl-teaser"]')
  ));
}

function isVisibleKlaviyoPopup(node) {
  if (node.hasAttribute(KL_STALE)) return false;
  const style = window.getComputedStyle(node);
  // While the drawer is open, yanaa-drawer.css hides every pop-up (visibility), so only display and
  // opacity (Klaviyo's own open/close animation) say whether it is open.
  const hiddenByDrawer = isDrawerOverlayActive();
  return style.display !== 'none' && (hiddenByDrawer || style.visibility !== 'hidden') && Number(style.opacity) > 0.05;
}

function getOpenKlaviyoOverlays() {
  return getKlaviyoPopups().filter(isVisibleKlaviyoPopup);
}

function isKlaviyoFormElement(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;

  if (node.matches?.('.klaviyo-close-form, .klaviyo-form, form.klaviyo-form, [aria-label="Close dialog"], [class*="klaviyo-form"]')) return true;

  const className = typeof node.className === 'string' ? node.className : '';
  const hint = `${node.id} ${className} ${(node.getAttribute('aria-label') || '')}`.toLowerCase();
  return /klaviyo-close|klaviyo-form/.test(hint);
}

function isOverlayWidget(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  if (isKlaviyoFormElement(node)) return false;

  const hint = [
    node.id,
    typeof node.className === 'string' ? node.className : '',
    node.tagName,
    node.getAttribute('src') || '',
  ].join(' ').toLowerCase();

  return /omnisend|soundest|kl-teaser|form-embed|app-embed-container/.test(hint);
}

function closeOpenKlaviyoForms() {
  // Only close a pop-up that is open, through its own close button. "Close teaser" matches
  // .klaviyo-close-form too, and clicking it opens the signup pop-up.
  getOpenKlaviyoOverlays().forEach((overlay) => {
    const button = [...overlay.querySelectorAll('button.klaviyo-close-form, button[aria-label="Close dialog"]')]
      .find((node) => !/teaser/i.test(node.getAttribute('aria-label') || '') && !node.closest('[class*="kl-teaser"]'));
    try {
      if (button) button.click();
      else document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    } catch (_) {}
  });
}

// A1 safety net, run after every close: if Klaviyo leaves the closed pop-up in place (opacity 0 but
// still catching taps), let taps through and give the page its scroll back. Undone on the next open.
function cleanupStaleKlaviyoPopups() {
  if (klaviyoLastOpen > klaviyoLastClose) return;
  let anyOpen = false;
  getKlaviyoPopups().forEach((node) => {
    if (node.hasAttribute(KL_STALE)) return;
    const style = window.getComputedStyle(node);
    if (style.display === 'none') return;
    if (Number(style.opacity) <= 0.05 || style.visibility === 'hidden') {
      node.setAttribute(KL_STALE, '');
      node.style.setProperty('pointer-events', 'none', 'important');
      node.style.setProperty('visibility', 'hidden', 'important');
    } else {
      anyOpen = true;
    }
  });
  if (!anyOpen) document.body.classList.remove('klaviyo-prevent-body-scrolling');
}

function releaseStaleKlaviyoPopups() {
  document.querySelectorAll(`[${KL_STALE}]`).forEach((node) => {
    node.removeAttribute(KL_STALE);
    node.style.removeProperty('pointer-events');
    node.style.removeProperty('visibility');
  });
  document.querySelectorAll(`[${KL_CLOSING}]`).forEach((node) => node.removeAttribute(KL_CLOSING));
}

// The pop-up(s) a klaviyoForms event is about (Klaviyo tags each form with data-testid="klaviyo-form-<formId>").
function getKlaviyoPopupsForEvent(event) {
  const popups = getKlaviyoPopups();
  const formId = event.detail?.formId;
  if (!formId) return popups;
  const own = popups.filter((node) => [...node.querySelectorAll('form[data-testid]')]
    .some((form) => form.getAttribute('data-testid') === `klaviyo-form-${formId}`));
  return own.length ? own : popups;
}

function onKlaviyoFormsEvent(event) {
  const type = event.detail?.type;
  if (type === 'open') {
    klaviyoLastOpen = Date.now();
    releaseStaleKlaviyoPopups();
    // A pop-up never stays over the open drawer: close it as soon as it has rendered.
    if (isDrawerOverlayActive()) [0, 300, 900].forEach((ms) => setTimeout(closeOpenKlaviyoForms, ms));
  } else if (type === 'close') {
    klaviyoLastClose = Date.now();
    // Klaviyo fades the closed pop-up out and can leave it in place: from the close on, taps go through to
    // the page (« Paiement », « Ajouter au panier »). Lifted on the next open.
    getKlaviyoPopupsForEvent(event).forEach((node) => node.setAttribute(KL_CLOSING, ''));
    [600, 1500, 3000].forEach((ms) => setTimeout(cleanupStaleKlaviyoPopups, ms));
  }
}

function resetKlaviyoInlineStyles() {
  document.querySelectorAll(`[${CART_WIDGET_HIDDEN}]`).forEach((node) => {
    showOverlayWidget(node);
  });
}

function hideOverlayWidget(node) {
  if (!node || node.hasAttribute(CART_WIDGET_HIDDEN)) return;

  node.setAttribute(CART_WIDGET_HIDDEN, '1');
  // Klaviyo finishes closing its pop-up on the teaser's animationend, which never fires under
  // display:none, so the teaser is only made invisible and untappable.
  if (!node.matches?.('[class*="kl-teaser"]')) node.style.setProperty('display', 'none', 'important');
  node.style.setProperty('visibility', 'hidden', 'important');
  node.style.setProperty('opacity', '0', 'important');
  node.style.setProperty('pointer-events', 'none', 'important');
  node.style.setProperty('z-index', '-1', 'important');
}

function showOverlayWidget(node) {
  if (!node?.hasAttribute(CART_WIDGET_HIDDEN)) return;

  node.style.removeProperty('display');
  node.style.removeProperty('visibility');
  node.style.removeProperty('opacity');
  node.style.removeProperty('pointer-events');
  node.style.removeProperty('z-index');
  node.removeAttribute(CART_WIDGET_HIDDEN);
}

function scanOverlayWidgets(root = document) {
  const widgets = new Set();

  root.querySelectorAll?.(
    '[id*="omnisend"], [id*="Omnisend"], [class*="omnisend"], [class*="Omnisend"], ' +
    '[id*="soundest"], [class*="soundest"], [id^="app-embed-container-"], form-embed, ' +
    '.needsclick[class*="kl-teaser"], [class*="kl-teaser"], iframe[src*="omnisend"], ' +
    'iframe[src*="soundest"]'
  ).forEach((node) => widgets.add(node));

  if (root.nodeType === Node.ELEMENT_NODE && isOverlayWidget(root)) {
    widgets.add(root);
  }

  return widgets;
}

function syncCartOverlayWidgets() {
  const hide = isDrawerOverlayActive();

  if (hide) {
    closeOpenKlaviyoForms();
  } else {
    cleanupStaleKlaviyoPopups();
  }

  scanOverlayWidgets().forEach((node) => {
    if (hide) {
      hideOverlayWidget(node);
    } else {
      showOverlayWidget(node);
    }
  });

  if (!hide) {
    resetKlaviyoInlineStyles();
  }
}

function inspectAddedOverlayWidget(node) {
  if (!isDrawerOverlayActive()) return;

  if (node.nodeType === Node.ELEMENT_NODE) {
    // The pop-up's dialog sits two levels below <body>, so look inside the added node too.
    const dialog = node.matches?.('[role="dialog"]') ? node : node.querySelector?.('[role="dialog"]');
    if (dialog && getKlaviyoPopups().includes(dialog)) {
      [0, 300].forEach((ms) => setTimeout(closeOpenKlaviyoForms, ms));
      watchKlaviyoPopupVisibility(dialog);
      return;
    }

    if (isOverlayWidget(node)) hideOverlayWidget(node);
    scanOverlayWidgets(node).forEach(hideOverlayWidget);
  }
}

function watchKlaviyoPopupVisibility(node) {
  if (!(node instanceof Element) || node.__yanaaKlaviyoWatch) return;
  node.__yanaaKlaviyoWatch = true;
  const observer = new MutationObserver(() => {
    if (!isDrawerOverlayActive()) return;
    if (getOpenKlaviyoOverlays().includes(node) || getOpenKlaviyoOverlays().some((overlay) => overlay.contains(node))) {
      closeOpenKlaviyoForms();
    }
  });
  observer.observe(node, { attributes: true, attributeFilter: ['style', 'class'] });
}

if (!window.__cartDrawerOverlayWidgetsBound) {
  window.__cartDrawerOverlayWidgetsBound = true;

  const bodyObserver = new MutationObserver((mutations) => {
    const relevant = mutations.some(
      (mutation) => mutation.attributeName === 'class'
        && drawerBodyClassChanged(mutation.oldValue || '', document.body.className)
    );

    if (relevant) syncCartOverlayWidgets();
  });
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    attributeOldValue: true,
  });

  const domObserver = new MutationObserver((mutations) => {
    if (!isDrawerOverlayActive()) return;

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(inspectAddedOverlayWidget);
    });
  });
  domObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('klaviyoForms', onKlaviyoFormsEvent);

  document.addEventListener('DOMContentLoaded', syncCartOverlayWidgets);
  document.addEventListener('cartdrawer:opened', syncCartOverlayWidgets);
  document.addEventListener('cartdrawer:content-updated', syncCartOverlayWidgets);
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (isDrawerOverlayActive()) return;

    const openOverlays = getOpenKlaviyoOverlays();
    if (!openOverlays.length) return;
    if (target.closest('.klaviyo-close-form, [class*="klaviyo-close"]')) return;
    if (openOverlays.some((overlay) => overlay.contains(target))) return;

    closeOpenKlaviyoForms();
  });
  document.addEventListener('cartdrawer:opened', scheduleMiniCartMainScroll);
  document.addEventListener('cartdrawer:content-updated', scheduleMiniCartMainScroll);
  syncCartOverlayWidgets();
}

function scheduleMiniCartMainScroll() {
  const miniCart = document.getElementById('mini-cart');
  if (!miniCart?.syncMainScroll) return;

  const run = () => miniCart.syncMainScroll({ scrollToBottom: miniCart.autoScrollMain });
  requestAnimationFrame(run);
  setTimeout(run, 120);
  setTimeout(run, 500);
  setTimeout(run, 1200);
}
